// Krug 5 (K12, docs/KRUG5_balans.md): meals. Food is no longer eaten by the hour; the Workshop cooks
// it into meals, and a survivor eats one before an ordinary expedition for an edge on that run.
// One meal per run, picked on the order sheet and taken from Storage on sending (not given back on a
// Recall or a defeat). The horde, raids and the first two runs take none. Meals stay out of Power and
// matchmaking, as the stance and the Stim do. `mealEffectPct` (Iron Stomach, Camper) makes them
// stronger, at most +100%.
//
// Package 4, part O (owner, 7. 10. 2026; "Obroci, hrana ima previše viška"): seven new meals named by the book of the
// world (Station 9 Chili, Black Coffee, Halcyon Protein Bar, Canned Peaches, Fever Broth, Night Stew, Horde Ration), and
// every meal by the survivor's tier: on a higher tier it costs more Food (T4 twice T1) and does a little more (T4 +30%).
// The Horde Ration is eaten when joining the horde and lasts for the rest of that horde (not on ordinary runs).

import type { ConsumableId, StackKind, ZoneId } from './types';

export type MealId = Extract<
  ConsumableId,
  'ration' | 'dried_jerky' | 'hot_stew' | 'cold_beans' | 'canned_feast' | 'station9_chili' | 'black_coffee' | 'halcyon_bar' | 'canned_peaches' | 'fever_broth' | 'night_stew' | 'horde_ration'
>;

/** Every meal (cooked at the Workshop). */
export const MEAL_IDS: readonly MealId[] = ['ration', 'dried_jerky', 'hot_stew', 'cold_beans', 'canned_feast', 'station9_chili', 'black_coffee', 'halcyon_bar', 'canned_peaches', 'fever_broth', 'night_stew', 'horde_ration'];

/** Package 4, part O: the meals an ordinary run takes (the Horde Ration is for the horde). */
export const RUN_MEAL_IDS: readonly MealId[] = MEAL_IDS.filter((m) => m !== 'horde_ration');

/** The seven meals of package 4, part O. */
export const P4_MEAL_IDS: readonly MealId[] = ['station9_chili', 'black_coffee', 'halcyon_bar', 'canned_peaches', 'fever_broth', 'night_stew', 'horde_ration'];

export const MEAL_RULES = {
  /** The most `mealEffectPct` adds up to (+100%). */
  maxBoost: 1,
  /** Ration: max HP ×(1 + this) for the run; HP at departure rises by the same amount. */
  ration: 0.1,
  /** Dried Jerky: added to the escape bonus (the 10–90% limit stays). */
  dried_jerky: 0.1,
  /** Hot Stew: heals this share of max HP after every won fight (no `healingPct`, as other after-fight heals). K12.10, round 2: 5% → 3%. */
  hot_stew: 0.03,
  /** Cold Beans: the encounter chance ×(1 − this) (not the NPC fights of a tick). */
  cold_beans: 0.1,
  /** Canned Feast: added to the run's XP multiplier (not contracts, crafts or the horde). */
  canned_feast: 0.1,
  /** Package 4, part O: Station 9 Chili, defense ×(1 + this) for the run. */
  station9_chili: 0.1,
  /** Black Coffee: added to initiative (as the load and the weapon class add to it). */
  black_coffee: 0.1,
  /** Halcyon Protein Bar: damage ×(1 + this). */
  halcyon_bar: 0.08,
  /** Canned Peaches: the chance of an injury after a fight ×(1 − this). */
  canned_peaches: 0.3,
  /** Fever Broth: the chance of a fever (a Deep Cut gone bad, an event's) ×(1 − this). */
  fever_broth: 0.5,
  /** Night Stew: the HP a quiet tick of a long run (2 h or more) gives back ×(1 + this). */
  night_stew: 0.5,
  /** Horde Ration: damage in the horde ×(1 + this) until that horde ends. */
  horde_ration: 0.15,
} as const;

/**
 * Package 4, part O: a meal by the survivor's tier (`wearableTier` of the level): the Food in its recipe × `food`, its
 * effect × `effect` (T1, T2, T3, T4). The other material of a recipe stays as it is.
 */
export const MEAL_TIER = { on: true, food: [1, 4 / 3, 5 / 3, 2] as const, effect: [1, 1.1, 1.2, 1.3] as const };

export const mealTierFood = (tier: number) => (MEAL_TIER.on ? MEAL_TIER.food[Math.max(1, Math.min(4, tier)) - 1] : 1);
export const mealTierEffect = (tier: number) => (MEAL_TIER.on ? MEAL_TIER.effect[Math.max(1, Math.min(4, tier)) - 1] : 1);

export const isMeal = (k: StackKind | string | null | undefined): k is MealId => !!k && (MEAL_IDS as readonly string[]).includes(k);
export const isRunMeal = (k: StackKind | string | null | undefined): k is MealId => !!k && (RUN_MEAL_IDS as readonly string[]).includes(k);

/** `mealEffectPct` as a multiplier's addition: 0.5 for Iron Stomach alone, never above +100%. */
export function mealBoost(mealEffectPct: number): number {
  return Math.max(0, Math.min(MEAL_RULES.maxBoost, mealEffectPct / 100));
}

/** Package 4, part O: the boost with the tier in: (1 + perks) × the tier's effect − 1 (T4 with Iron Stomach: +95%). */
export function mealBoostWithTier(mealEffectPct: number, tier: number): number {
  return (1 + mealBoost(mealEffectPct)) * mealTierEffect(tier) - 1;
}

export interface MealBonus {
  /** Max HP for the run: ×(1 + hpMaxPct). */
  hpMaxPct: number;
  /** Added to the escape bonus. */
  escape: number;
  /** Share of max HP healed after a won fight. */
  winHeal: number;
  /** The encounter chance is multiplied by this. */
  encounterMult: number;
  /** Added to the run's XP multiplier. */
  xp: number;
  /** Package 4, part O: defense and damage ×(1 + this); added to initiative. */
  defensePct: number;
  damagePct: number;
  initiative: number;
  /** The chance of an injury and of a fever × this. */
  injuryMult: number;
  feverMult: number;
  /** A long run's quiet tick heals × (1 + this). */
  restPct: number;
  /** Damage in the horde × (1 + this). */
  hordeDamagePct: number;
}

/** What a meal does on a run, with the boost (`mealBoost`, the tier in since part O) in. */
export function mealBonus(meal: MealId | null | undefined, boost = 0): MealBonus {
  const m = 1 + Math.max(0, boost);
  const b: MealBonus = { hpMaxPct: 0, escape: 0, winHeal: 0, encounterMult: 1, xp: 0, defensePct: 0, damagePct: 0, initiative: 0, injuryMult: 1, feverMult: 1, restPct: 0, hordeDamagePct: 0 };
  if (meal === 'ration') b.hpMaxPct = MEAL_RULES.ration * m;
  else if (meal === 'dried_jerky') b.escape = MEAL_RULES.dried_jerky * m;
  else if (meal === 'hot_stew') b.winHeal = MEAL_RULES.hot_stew * m;
  else if (meal === 'cold_beans') b.encounterMult = Math.max(0, 1 - MEAL_RULES.cold_beans * m);
  else if (meal === 'canned_feast') b.xp = MEAL_RULES.canned_feast * m;
  else if (meal === 'station9_chili') b.defensePct = MEAL_RULES.station9_chili * m;
  else if (meal === 'black_coffee') b.initiative = MEAL_RULES.black_coffee * m;
  else if (meal === 'halcyon_bar') b.damagePct = MEAL_RULES.halcyon_bar * m;
  else if (meal === 'canned_peaches') b.injuryMult = Math.max(0, 1 - MEAL_RULES.canned_peaches * m);
  else if (meal === 'fever_broth') b.feverMult = Math.max(0, 1 - MEAL_RULES.fever_broth * m);
  else if (meal === 'night_stew') b.restPct = MEAL_RULES.night_stew * m;
  else if (meal === 'horde_ration') b.hordeDamagePct = MEAL_RULES.horde_ration * m;
  return b;
}

const pct = (x: number) => {
  const v = Math.round(x * 1000) / 10;
  return `${Number.isInteger(v) ? v : v.toFixed(1)}%`;
};

/** The effect in words, with the boost in: `+15% max HP`, `Heals 5% HP after every won fight`. */
export function mealEffectText(meal: MealId, boost = 0): string {
  const b = mealBonus(meal, boost);
  switch (meal) {
    case 'ration':
      return `+${pct(b.hpMaxPct)} max HP`;
    case 'dried_jerky':
      return `+${pct(b.escape)} escape`;
    case 'hot_stew':
      return `Heals ${pct(b.winHeal)} HP after every won fight`;
    case 'cold_beans':
      return `−${pct(1 - b.encounterMult)} encounter chance`;
    case 'canned_feast':
      return `+${pct(b.xp)} XP from the run`;
    case 'station9_chili':
      return `+${pct(b.defensePct)} defense`;
    case 'black_coffee':
      return `+${pct(b.initiative)} initiative`;
    case 'halcyon_bar':
      return `+${pct(b.damagePct)} damage`;
    case 'canned_peaches':
      return `−${pct(1 - b.injuryMult)} injury chance`;
    case 'fever_broth':
      return `−${pct(1 - b.feverMult)} fever chance`;
    case 'night_stew':
      return `+${pct(b.restPct)} rest on quiet stretches of a long run`;
    case 'horde_ration':
      return `+${pct(b.hordeDamagePct)} damage in tonight's horde`;
  }
}

/** Only ordinary runs take a meal: not the horde, a raid or the first two (onboarding) runs. */
export function takesMeal(kind: 'normal' | 'raid' | 'horde', onboarding: boolean): boolean {
  return kind === 'normal' && !onboarding;
}

/** Package 4, part O: what the order sheet knows of a run when it suggests a meal. */
export interface MealContext {
  zone: ZoneId;
  danger: number;
  /** The zone's PvP (open: other survivors meet you here). */
  pvpOpen: boolean;
  minutes: number;
  /** The survivor is below the zone's recommended level or Power (`zonePowerBoost` > 0). */
  tough: boolean;
  /** A horde tonight (a Horde Ration is for it, not for this run). */
  hordeTonight: boolean;
  /** An injury on now (no second one can come), a Deep Cut on (a fever can), a fever on. */
  injury: boolean;
  deepCut: boolean;
  fever: boolean;
  /** 9. 10. 2026 (owner): WARDEN's units in the zone (its drones find a feverish survivor more often). */
  warden?: boolean;
  /** A tank build (VIT is the survivor's highest attribute). */
  tank?: boolean;
  /** The order sheet's stance is Aggressive (from the sheet, as the duration). */
  aggressive?: boolean;
}

/**
 * Package 4, part O: the meal the order sheet suggests for a run and why, the situation's own meal first (owner, 9. 10.
 * 2026): a Deep Cut that can go bad (Fever Broth), a zone that is tough for the survivor (Halcyon Protein Bar), the
 * Aggressive stance without an injury on (Canned Peaches), WARDEN's zones without a fever on (Fever Broth), open PvP
 * (Black Coffee), a tank build (Station 9 Chili), a long run (Night Stew), then a tough zone's Chili, danger 4+ without an
 * injury (Canned Peaches) and the meals before (K12). Only meals in Storage.
 */
export function mealSuggestion(ctx: MealContext, have: (m: MealId) => boolean): { id: MealId; why: string } | null {
  const want: [MealId, string][] = [];
  if (ctx.deepCut && !ctx.fever) want.push(['fever_broth', 'Your Deep Cut can turn into a fever.']);
  if (ctx.tough) want.push(['halcyon_bar', 'This zone is tough for you.']);
  if (ctx.aggressive && !ctx.injury) want.push(['canned_peaches', 'You go looking for fights: fewer injuries.']);
  if (ctx.warden && !ctx.fever) want.push(['fever_broth', 'WARDEN scans for fevers here: half the chance of one.']);
  if (ctx.pvpOpen) want.push(['black_coffee', 'Other survivors here: strike first.']);
  if (ctx.tank) want.push(['station9_chili', 'Built to take hits: more defense.']);
  if (ctx.minutes >= 120) want.push(['night_stew', 'A long run: more HP back on its quiet stretches.']);
  if (ctx.tough) want.push(['station9_chili', 'This zone is tough for you.']);
  if (ctx.danger >= 4 && !ctx.injury) want.push(['canned_peaches', 'A dangerous zone: fewer injuries.']);
  if (ctx.pvpOpen) want.push(['cold_beans', 'Other survivors here: meet fewer of them.']);
  if (ctx.minutes >= 40) want.push(['hot_stew', 'A longer run with fights in it.']);
  if (ctx.danger >= 4) want.push(['dried_jerky', 'A dangerous zone: a better chance to get away.']);
  want.push(['ration', 'More HP for the run.']);
  const hit = want.find(([m]) => have(m));
  return hit ? { id: hit[0], why: hit[1] } : null;
}
